import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Switch,
  Text,
  Tooltip
} from "@chakra-ui/react";
import { useState } from "react";
import { GetLayout } from "components/get-layout";
import { Toast } from "components/toast";
import {
  CustomTableColor,
  CustomTableColorColumn
} from "components/dynamic-table/color-table";
import { ModelosPipelineEndpoints, PipelineModeloDTO } from "./utils";
import { ConvenioBadge } from "./convenio-badge";
import { TipoOperacaoBadge } from "./tipo-operacao-badge";
import { MAX_VISIVEL } from "./constants";
import {
  blockChainLabel,
  formatDate,
  getIconComponent,
  parseBlocksFromSequencia
} from "./helpers";

export function ModelosListView({
  modelos,
  isLoading,
  onNew,
  onEdit,
  onDeleted,
  onStatusToggled
}: {
  modelos: PipelineModeloDTO[];
  isLoading: boolean;
  onNew: () => void;
  onEdit: (m: PipelineModeloDTO) => void;
  onDeleted: (id: number) => void;
  onStatusToggled: (m: PipelineModeloDTO) => void;
}) {
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const handleDelete = async (m: PipelineModeloDTO) => {
    if (!m.id) return;
    setDeletingId(m.id);
    try {
      await ModelosPipelineEndpoints.remove(m.id);
      Toast({ title: "Modelo excluído", status: "success" });
      onDeleted(m.id);
    } catch {
      Toast({ title: "Erro ao excluir modelo", status: "error" });
    } finally {
      setDeletingId(null);
    }
  };

  const handleStatusToggle = async (m: PipelineModeloDTO) => {
    if (!m.id) return;
    setTogglingId(m.id);
    try {
      const nextStatus = m.status === "ATIVO" ? "INATIVO" : "ATIVO";
      await ModelosPipelineEndpoints.patchStatus(m.id, nextStatus);
      onStatusToggled({ ...m, status: nextStatus });
    } catch {
      Toast({ title: "Erro ao alterar status", status: "error" });
    } finally {
      setTogglingId(null);
    }
  };

  const columns: CustomTableColorColumn<PipelineModeloDTO>[] = [
    {
      name: "NOME",
      render: (m) => {
        const IconComp = getIconComponent(m.icone);
        const accentColor = m.cor ?? "#2F80ED";
        const softColor = `${accentColor}18`;
        return (
          <Flex align="center" gap="10px">
            <Box
              w="34px"
              h="34px"
              borderRadius="10px"
              bg={softColor}
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Icon as={IconComp} color={accentColor} w="16px" h="16px" />
            </Box>
            <Box>
              <Text fontSize="14px" fontWeight="600" color="#2D3748">
                {m.nome}
              </Text>
              {m.descricaoCurta && (
                <Text fontSize="12px" color="#718096" noOfLines={1}>
                  {m.descricaoCurta}
                </Text>
              )}
            </Box>
          </Flex>
        );
      }
    },
    {
      name: "CONVÊNIOS",
      render: (m) => <ConvenioBadge convenio={m.convenio} />
    },
    {
      name: "TIPO OPERAÇÃO",
      render: (m) => <TipoOperacaoBadge tipoOperacao={m.tipoOperacao} />
    },
    {
      name: "BLOCOS",
      render: (m) => {
        const blocks = parseBlocksFromSequencia(m.sequenciaBloco);
        const chainLabel = blockChainLabel(blocks);
        return (
          <Text fontSize="12px" color="#4A5568" noOfLines={1}>
            {chainLabel || "—"}
          </Text>
        );
      }
    },
    {
      name: "CRIADO EM",
      render: (m) => (
        <Text fontSize="13px" color="#4A5568">
          {formatDate(m.createdAt)}
        </Text>
      )
    },
    {
      name: "AÇÕES",
      render: (m) => {
        const isToggling = togglingId === m.id;
        const isDeleting = deletingId === m.id;
        return (
          <Flex gap="6px" align="center">
            <Tooltip label={m.status === "ATIVO" ? "Desativar" : "Ativar"}>
              <Button
                size="sm"
                borderRadius="10px"
                bg={m.status === "ATIVO" ? "#C6F6D5" : "#EDF2F7"}
                color={m.status === "ATIVO" ? "#276749" : "#718096"}
                border="1.5px solid"
                borderColor={m.status === "ATIVO" ? "#68D391" : "#CBD5E0"}
                leftIcon={
                  <Switch
                    size="sm"
                    colorScheme="green"
                    isChecked={m.status === "ATIVO"}
                    isReadOnly
                    pointerEvents="none"
                  />
                }
                _hover={{
                  bg: m.status === "ATIVO" ? "#9AE6B4" : "#E2E8F0"
                }}
                isLoading={isToggling}
                onClick={() => handleStatusToggle(m)}
              >
                {m.status === "ATIVO" ? "Ativo" : "Inativo"}
              </Button>
            </Tooltip>
            <Tooltip label="Editar">
              <IconButton
                aria-label="editar"
                icon={<EditIcon />}
                size="sm"
                bg="#2D3748"
                color="white"
                _hover={{ bg: "#1A202C" }}
                onClick={() => onEdit(m)}
              />
            </Tooltip>

            <Tooltip label="Excluir">
              <IconButton
                aria-label="excluir"
                icon={<DeleteIcon />}
                size="sm"
                variant="danger"
                colorScheme="red"
                isLoading={isDeleting}
                onClick={() => handleDelete(m)}
              />
            </Tooltip>
          </Flex>
        );
      }
    }
  ];

  return (
    <GetLayout
      title="Modelos de Pipeline"
      rightContent={
        <Flex align="center" gap="8px">
          <Badge
            px="10px"
            py="3px"
            borderRadius="20px"
            fontSize="12px"
            bg="#EDF2F7"
            color="#4A5568"
            fontWeight="600"
          >
            {modelos.length}/{MAX_VISIVEL * 2} modelos
          </Badge>
          <Button
            colorScheme="blue"
            leftIcon={<AddIcon />}
            onClick={onNew}
            bg="#343B44"
            _hover={{ bg: "#2D3440" }}
          >
            Novo Modelo
          </Button>
        </Flex>
      }
    >
      <CustomTableColor
        columns={columns}
        rows={modelos}
        isLoading={isLoading}
        showControls={false}
        emptyMessage="Nenhum modelo cadastrado ainda."
      />
    </GetLayout>
  );
}
